import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, type KeyboardEvent } from 'react';

import { sortBy } from 'lodash-es';
import { defineMessages, useIntl } from 'react-intl';
import { useFavoriteSecurities, useMixpanel, useRecentSymbols, useSecuritiesContext } from '../../contexts';
import { useDynamicCallback } from '../../hooks';
import { MixpanelEvent, MixpanelEventProperty } from '../../tokens';
import type { Security } from '../../types';
import { getGroup, getProductGroupLabel } from '../../utils';
import { Button, ButtonVariants, IconButton } from '../Button';
import { Flex, VStack } from '../Core';
import { CurrencyPair } from '../CurrencyPair';
import { Divider } from '../Divider';
import {
  AutocompleteDropdown,
  AutocompleteResult,
  BaseSelect,
  FormControlSizes,
  Input,
  useAutocompleteGroupSorter,
  useDropdownPopper,
  useSearchSelect,
  type AutocompleteGroup,
  type RenderResultFunc,
} from '../Form';
import { getGroupsMetadata } from '../Form/Autocomplete/utils';
import { NoResults, Title } from '../Form/AutocompleteDropdown/styles';
import { ALL_TAB } from '../Form/AutocompleteDropdown/types';
import { Icon, IconName } from '../Icons';
import { FormattedMessage } from '../Intl';
import { SecurityRenderer } from '../SecurityRenderer';
import { Text } from '../Text';
import { FavoritesButton, GroupsPrefixWrapper, StandardSecurityButton } from './styles';
import { SymbolSelectorAppearance, type SymbolSelectorProps } from './types';
import { createSortFilterOverride } from './utils';

const messages = defineMessages({
  filterSymbolList: {
    defaultMessage: 'Filter Symbol List',
    id: 'SymbolSelector.filterSymbolList',
  },
  favorites: {
    defaultMessage: 'Favorites',
    id: 'SymbolSelector.favorites',
  },
  noResultsFound: {
    defaultMessage: 'No Results Found',
    id: 'SymbolSelector.noResultsFound',
  },
  selectSymbol: {
    defaultMessage: 'Select symbol',
    id: 'SymbolSelector.selectSymbol',
  },
});

const LIST_HEIGHT = 400;
const MIN_MATCH_CHAR_LENGTH = 2;

const getLabel = (sec: Security) => {
  return sec.DisplaySymbol ?? sec.Symbol;
};
const getDescription = (sec: Security) => {
  return sec.Description;
};

const groupOrder = ['All', 'Spot', 'Perp', 'Future', 'Option', 'Multi', 'ETF', 'Swap', 'Calendar Spread', 'Prediction'];

const getDefaultListGroup = (security: Security) => getProductGroupLabel(getGroup(security));

/** WARNING: Be careful not to confuse this with **SearchSelect** this uses useSearchSelect, but is a top-level variant */
export const SymbolSelector = ({
  className,
  startOpen = false,
  autoFocus = false,
  symbol,
  onIsOpenChange,
  onSymbolChanged,
  availableSecurities,
  getGroup: getGroupProp,
  getTabGroup,
  disabled,
  dropdownPlacement = 'bottom-start',
  dropdownWidth,
  dropdownId,
  maxHeight = LIST_HEIGHT,
  size = FormControlSizes.Default,
  appearance: variant,
  portalize,
  showClear,
  showGroups = false,
  suppressGroupTabsOverflow,
  RenderGroupHeader,
  centerBasicSelect = false,
  showFavorites = false,
  showSelectedSymbolDescription: showDescription = true,
  securityRendererMaxWidth,
  suffix,
  suffixCb,
  placeholder,
  readOnly,
  invalid,
  touched,
  closeMenuRef,
  renderTrigger,
  closeOnInputBlur,
  isItemDisabled,
  menuStyles,
  ...props
}: SymbolSelectorProps) => {
  const mixpanel = useMixpanel();
  const { recentSymbolsList, updateRecentSymbols } = useRecentSymbols();
  const { searchableSecurities } = useSecuritiesContext();
  const { favoriteSecurities, setFavoriteSecurities } = useFavoriteSecurities();
  const favoriteSecuritiesSet = useMemo(() => new Set(favoriteSecurities), [favoriteSecurities]);
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);
  const { formatMessage } = useIntl();

  const searchableItems = useMemo(() => {
    const securitiesList = availableSecurities ? availableSecurities : searchableSecurities;

    const recentIndexMap = new Map(recentSymbolsList.slice(0, 5).map((symbol, idx) => [symbol, idx]));

    return sortBy(securitiesList, s => {
      // put favorites at the top
      if (showFavorites && favoriteSecuritiesSet.has(s.Symbol)) {
        return -1;
      }
      // Symbols used more often will be adjusted towards the top
      // based on their position index within recent symbols
      const recentIndex = recentIndexMap.get(s.Symbol);
      if (recentIndex != null) {
        return recentIndex;
      } else if (s.Rank) {
        // Fallback to secmaster Rank (* 100 to put it below recency index)
        return s.Rank * 100;
      }
    });
  }, [recentSymbolsList, showFavorites, favoriteSecuritiesSet, availableSecurities, searchableSecurities]);

  const security = useMemo(() => {
    return searchableItems.find(s => s.Symbol === symbol);
  }, [searchableItems, symbol]);

  const handleChange = useCallback(
    (security?: Security) => {
      if (security) {
        updateRecentSymbols(security.Symbol);
        onSymbolChanged(security.Symbol);
      }
    },
    [updateRecentSymbols, onSymbolChanged]
  );

  const inputRef = useRef<HTMLInputElement>(null);

  const getListGroup = getGroupProp ?? getDefaultListGroup;

  const items = useMemo((): Security[] | AutocompleteGroup<Security>[] => {
    if (!showGroups) {
      return searchableItems;
    }
    const groups = [...getGroupsMetadata(searchableItems, getListGroup).values()];
    if (!showOnlyFavorites) {
      return groups;
    }
    return groups.map(group => ({
      ...group,
      items: group.items.filter(s => favoriteSecuritiesSet.has(s.Symbol)),
    }));
  }, [searchableItems, favoriteSecuritiesSet, showGroups, showOnlyFavorites, getListGroup]);

  const groupSorter = useAutocompleteGroupSorter(groupOrder);

  const sortFilterOverride = useMemo(
    () => createSortFilterOverride(recentSymbolsList, MIN_MATCH_CHAR_LENGTH),
    [recentSymbolsList]
  );

  const searchSelect = useSearchSelect<Security>({
    selection: security,
    items,
    getGroup: showGroups ? getListGroup : undefined,
    getTabGroup: showGroups ? getTabGroup : undefined,
    inputRef,
    initialSortByLabel: false,
    getLabel,
    getDescription,
    sortFilterOverride,
    onChange: handleChange,
    inputValueChangeOnItemSelection: 'clear',
    /**
     * This prop should be used very intentionally by its consumer. For now,
     * there is only 1 consumer in the whole codebase that uses it from the
     * SymbolSelector layer itself, and that's only so that it can be notified
     * on the chance that the selector closed without any value being picked,
     * so that it can reset a piece of its own internal state.
     */
    onIsOpenChange,
    scrollIntoView: target => {
      if (target) {
        (target as any)['scrollIntoViewIfNeeded']
          ? (target as any)['scrollIntoViewIfNeeded']()
          : target.scrollIntoView(false);
      }
    },
    groupSorter,
    initialIsOpen: startOpen,
    closeOnInputBlur,
    isItemDisabled,
  });

  const { getInputProps, openMenu, closeMenu, isOpen } = searchSelect;

  const triggerRef = useRef<HTMLElement | null>(null);
  const setTriggerRef = useCallback((node: HTMLElement | null) => {
    triggerRef.current = node;
  }, []);
  const didConsiderAutoFocus = useRef(false);
  useEffect(() => {
    if (didConsiderAutoFocus.current) {
      return;
    }
    didConsiderAutoFocus.current = true;
    if (autoFocus) {
      triggerRef.current?.focus();
    }
  }, [autoFocus]);

  useImperativeHandle(closeMenuRef, () => {
    return closeMenu;
  }, [closeMenu]);

  const handleDropdownKeyDown = useDynamicCallback((e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Escape' && isOpen) {
      e.stopPropagation();
    }
  });

  const dropdownReferenceElement = useRef<HTMLDivElement | null>(null);

  const dropdownPopper = useDropdownPopper({
    isOpen,
    dropdownWidth,
    dropdownPlacement: dropdownPlacement,
    referenceElement: dropdownReferenceElement.current ?? null,
    onClickOutside: closeMenu,
  });

  const renderGhostVariant = useCallback(() => {
    return (
      <StandardSecurityButton
        ref={setTriggerRef}
        ghost
        onClick={openMenu}
        disabled={disabled}
        type="button"
        endIcon={IconName.ChevronDown}
      >
        {symbol ? (
          <SecurityRenderer
            symbol={symbol}
            security={security}
            currency={security?.BaseCurrency}
            showDescription={showDescription}
            iconSize={showDescription ? 24 : 16}
            maxWidth={securityRendererMaxWidth}
          />
        ) : (
          <Text>
            <FormattedMessage {...messages.selectSymbol} />
          </Text>
        )}
      </StandardSecurityButton>
    );
  }, [disabled, openMenu, security, symbol, showDescription, securityRendererMaxWidth, setTriggerRef]);

  const renderCurrencyPairVariant = useCallback(() => {
    return (
      <Button
        ref={setTriggerRef}
        ghost
        onClick={openMenu}
        onFocus={openMenu}
        disabled={disabled}
        size={FormControlSizes.Small}
        endIcon={IconName.ChevronDown}
      >
        {security ? <CurrencyPair px={0} security={security} /> : <FormattedMessage {...messages.selectSymbol} />}
      </Button>
    );
  }, [openMenu, disabled, security, setTriggerRef]);

  const renderBasicVariant = useCallback(() => {
    return (
      <BaseSelect
        wrapperRef={setTriggerRef}
        isDropdownOpened={isOpen}
        onClick={openMenu}
        onFocus={openMenu}
        getLabel={getLabel}
        value={security}
        size={size}
        placeholder={placeholder ?? formatMessage(messages.selectSymbol)}
        clearable={showClear}
        onClearClick={() => onSymbolChanged(undefined)}
        disabled={disabled}
        centered={centerBasicSelect}
        readOnly={readOnly}
        invalid={invalid}
        touched={touched}
      />
    );
  }, [
    isOpen,
    openMenu,
    security,
    size,
    placeholder,
    formatMessage,
    showClear,
    disabled,
    centerBasicSelect,
    onSymbolChanged,
    readOnly,
    invalid,
    touched,
    setTriggerRef,
  ]);

  const renderSearchVariant = useCallback(() => {
    return (
      <IconButton
        ref={setTriggerRef}
        size={size}
        icon={IconName.Search}
        variant={isOpen ? ButtonVariants.Priority : ButtonVariants.Default}
        onMouseDown={isOpen ? e => e.stopPropagation() : undefined}
        onClick={isOpen ? closeMenu : openMenu}
        disabled={disabled}
        data-testid="search-all-symbol-selector"
      />
    );
  }, [openMenu, closeMenu, size, isOpen, disabled, setTriggerRef]);

  const toggleFavorite = useDynamicCallback((security: Security, isFavorite: boolean) => {
    const newFavorites = isFavorite
      ? favoriteSecurities.filter(symbol => symbol !== security.Symbol)
      : favoriteSecurities.concat([security.Symbol]);
    setFavoriteSecurities(newFavorites);
    mixpanel.track(MixpanelEvent.FavoriteSymbol);
  });

  const renderFavoriteIcon = useCallback(
    (security: Security) => {
      if (!showFavorites) {
        return null;
      }
      const isFavorite = favoriteSecuritiesSet.has(security.Symbol);
      return (
        <Icon
          icon={isFavorite ? IconName.StarSolid : IconName.Star}
          color={isFavorite ? 'yellow.lighten' : 'gray.080'}
          size="fontSizeLarge"
          onClick={e => {
            toggleFavorite(security, isFavorite);
            e.stopPropagation();
          }}
          p="spacing12"
          spaceBefore="-spacing12"
          data-testid="favorite-toggle"
        />
      );
    },
    [favoriteSecuritiesSet, toggleFavorite, showFavorites]
  );

  const renderResult: RenderResultFunc<Security> = useDynamicCallback((searchResults, disabled, searchString) =>
    AutocompleteResult(searchResults, disabled, searchString, renderFavoriteIcon, { matchAcrossHyphens: true })
  );

  const handleFavoritesClick = useCallback(
    (value: boolean) => {
      mixpanel.track(MixpanelEvent.FavoriteFilter, {
        [MixpanelEventProperty.Value]: value,
      });
      setShowOnlyFavorites(value);
    },
    [mixpanel, setShowOnlyFavorites]
  );

  const groupsPrefix = useMemo(() => {
    if (!showFavorites || !showGroups) {
      return null;
    }
    return (
      <GroupsPrefixWrapper>
        <IconButton
          ghost
          onClick={() => handleFavoritesClick(!showOnlyFavorites)}
          icon={showOnlyFavorites ? IconName.StarSolid : IconName.Star}
          color={showOnlyFavorites ? 'yellow.lighten' : 'gray.080'}
          className="toggle-favorites"
        />
        <Divider orientation="vertical" mr="spacing4" ml="spacing4" />
      </GroupsPrefixWrapper>
    );
  }, [showFavorites, showGroups, showOnlyFavorites, handleFavoritesClick]);

  const renderEmpty = useDynamicCallback(() => {
    if (!showGroups || !showOnlyFavorites) {
      return (
        <NoResults data-testid="dropdown-no-results">
          <Title>
            <FormattedMessage {...messages.noResultsFound} />
          </Title>
        </NoResults>
      );
    } else {
      const selectedGroup = searchSelect.tabs.items[searchSelect.tabs.selectedIndex].group;
      const group = selectedGroup === ALL_TAB.group ? null : selectedGroup;
      return (
        <VStack data-testid="dropdown-no-results" gap="spacing16" p="spacing24">
          <Title>
            There are no favorites{' '}
            {group && (
              <>
                for <Text color="colorTextAttention">{group}</Text>
              </>
            )}
          </Title>
          <Button startIcon={IconName.Reply} onClick={() => handleFavoritesClick(false)}>
            View All {group} Symbols
          </Button>
        </VStack>
      );
    }
  });

  return (
    <Flex
      className={className}
      data-testid="symbol-selector"
      data-disabled={disabled}
      {...props}
      ref={dropdownReferenceElement}
      position="relative"
    >
      {renderTrigger
        ? renderTrigger({ openMenu, isOpen, disabled, security })
        : variant === SymbolSelectorAppearance.Basic
          ? renderBasicVariant()
          : variant === SymbolSelectorAppearance.Search
            ? renderSearchVariant()
            : variant === SymbolSelectorAppearance.CurrencyPair
              ? renderCurrencyPairVariant()
              : renderGhostVariant()}
      <div>
        <AutocompleteDropdown
          {...searchSelect}
          {...dropdownPopper}
          menuStyles={menuStyles}
          renderResult={renderResult}
          size={FormControlSizes.Default}
          maxHeight={maxHeight}
          portalize={portalize}
          onKeyDown={handleDropdownKeyDown}
          showGroupTabs={showGroups}
          suppressGroupTabsOverflow={suppressGroupTabsOverflow}
          groupSorter={groupSorter}
          RenderGroupHeader={RenderGroupHeader}
          suffix={suffix}
          suffixCb={suffixCb}
          renderEmpty={renderEmpty}
          groupsPrefix={groupsPrefix}
          id={dropdownId}
          childrenAboveResults={
            <Input
              {...getInputProps(
                { ref: inputRef },
                {
                  // If portalize is used, the ref error is suppressed since
                  // the ref is not available in the same context
                  suppressRefError: portalize,
                }
              )}
              data-testid="symbol-selector-input"
              placeholder={formatMessage(messages.filterSymbolList)}
              disabled={disabled}
              size={FormControlSizes.Default}
              suffix={
                showOnlyFavorites && (
                  <FavoritesButton
                    startIcon={IconName.StarSolid}
                    endIcon={IconName.Close}
                    size={FormControlSizes.Tiny}
                    onClick={() => handleFavoritesClick(false)}
                  >
                    <FormattedMessage {...messages.favorites} />
                  </FavoritesButton>
                )
              }
            />
          }
        />
      </div>
    </Flex>
  );
};
